import { useEffect } from 'react'
import { shouldPinRoster } from '@sportninja/common/features/competition/operations/utils/shouldPinRoster'
import { useRosterContext } from 'src/pages/Team/UnifiedRoster/context/RosterContext'

interface ForceRosterSelectionProps {
  /** The roster resolved from the selected division. */
  rosterId: string
}

/**
 * Pins `UnifiedRoster` to the roster belonging to the division the user opened.
 *
 * `useRosterSelect` (`src/components/List/RosterSelectNew.jsx`) picks the initial
 * roster with a heuristic the Operations tab cannot satisfy:
 *
 * 1. If an `sn_schedule` query param is present — and it may be, left in the URL
 *    by Standings/Statistics/Financial, which all set it — it matches on
 *    `schedule_uid` and otherwise falls back to `rosters[0]`. In that branch
 *    `existingRosterIdFromLocationState` is never consulted at all. A team with
 *    rosters in several competitions then shows the wrong division's players.
 * 2. Otherwise it reads `existingRosterIdFromLocationState` on FIRST RENDER only
 *    and clears it after one use.
 *
 * Rather than rewrite that hook — it is shared with the Team page and the billing
 * table — this corrects the selection from the outside. `setSelectedRosterId`
 * dispatches into the same state the fetch effect watches, so the
 * `/players` and `/team-officials` requests fire for the right roster.
 */
const ForceRosterSelection = ({ rosterId }: ForceRosterSelectionProps) => {
  'use memo'

  const { rosters, selectedRosterId, setSelectedRosterId } = useRosterContext()

  useEffect(() => {
    const availableIds = rosters.map((roster: { id: string }) => roster.id)
    if (
      shouldPinRoster({
        availableIds,
        wantedId: rosterId,
        currentId: selectedRosterId,
      })
    ) {
      setSelectedRosterId(rosterId)
    }
  }, [rosters, rosterId, selectedRosterId, setSelectedRosterId])

  return null
}

export default ForceRosterSelection
